import { makeAutoObservable, runInAction, computed } from 'mobx'
import { Store } from './index.stores'
import { ISubscriptionStatus, IPlan, IInvoice } from '@typings'
import { BILLING_CONSTANTS } from '@constants'

export class BillingStore {
  store: Store
  status: ISubscriptionStatus | null = null
  plans: IPlan[] = []
  invoices: IInvoice[] = []
  loading = false
  error: string | null = null

  constructor(store: Store) {
    this.store = store
    makeAutoObservable(this)
  }

  /**
   * Fetch current subscription status
   */
  async fetchStatus() {
    this.loading = true
    this.error = null
    try {
      const status = await this.store.api.billing.getStatus()
      runInAction(() => {
        this.status = status
        this.loading = false
      })
      return status
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_LOAD_STATUS
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Fetch available plans
   */
  async fetchPlans() {
    this.loading = true
    this.error = null
    try {
      const plans = await this.store.api.billing.getPlans()
      runInAction(() => {
        this.plans = plans
        this.loading = false
      })
      return plans
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_LOAD_PLANS
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Subscribe to a plan (start trial or upgrade)
   */
  async subscribe(priceId: string, coupon?: string): Promise<string> {
    this.loading = true
    this.error = null
    try {
      const { checkout_url } = await this.store.api.billing.subscribe({
        price_id: priceId,
        coupon,
      })
      runInAction(() => {
        this.loading = false
      })
      return checkout_url
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_CREATE_CHECKOUT
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Reset billing store
   */
  reset() {
    this.status = null
    this.plans = []
    this.invoices = []
    this.loading = false
    this.error = null
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    this.loading = true
    this.error = null
    try {
      const result = await this.store.api.billing.cancelSubscription()
      // Refresh status to get updated ends_at date
      await this.fetchStatus()
      runInAction(() => {
        this.loading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_CANCEL_SUBSCRIPTION
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Resume cancelled subscription
   */
  async resumeSubscription() {
    this.loading = true
    this.error = null
    try {
      await this.store.api.billing.resumeSubscription()
      // Refresh status to get updated state
      await this.fetchStatus()
      runInAction(() => {
        this.loading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_RESUME_SUBSCRIPTION
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Fetch invoices
   */
  async fetchInvoices() {
    this.loading = true
    this.error = null
    try {
      const invoices = await this.store.api.billing.getInvoices()
      runInAction(() => {
        this.invoices = invoices
        this.loading = false
      })
      return invoices
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || BILLING_CONSTANTS.ERRORS.FAILED_LOAD_INVOICES
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Get Stripe Customer Portal URL
   */
  async getPortalUrl(): Promise<string> {
    const { url } = await this.store.api.billing.getPortalUrl()
    return url
  }

  // ==================== COMPUTED GETTERS ====================

  /**
   * Check if user has PRO access (paid or trial)
   */
  @computed
  get isPro(): boolean {
    return this.status?.status === 'pro_paid' || this.status?.status === 'pro_trial'
  }

  /**
   * Check if user is on trial
   */
  @computed
  get isOnTrial(): boolean {
    return this.status?.is_on_trial ?? false
  }

  /**
   * Check if user is on free tier
   */
  @computed
  get isFree(): boolean {
    return this.status?.tier === 'free' || this.status?.status === 'free_active'
  }

  /**
   * Check if user is in read-only mode
   */
  @computed
  get isReadOnly(): boolean {
    return this.status?.status === 'free_readonly'
  }

  /**
   * Check if user can edit documents
   */
  @computed
  get canEditDocuments(): boolean {
    return this.status?.can_edit_documents ?? false
  }

  /**
   * Check if user can finalize documents
   */
  @computed
  get canFinalizeDocuments(): boolean {
    return this.status?.can_finalize_documents ?? false
  }

  /**
   * Get trial days remaining
   */
  @computed
  get trialDaysRemaining(): number | null {
    return this.status?.trial_days_remaining ?? null
  }

  /**
   * Get user status for display
   */
  @computed
  get userStatus(): 'pro_paid' | 'pro_trial' | 'free_readonly' | 'free_active' {
    return this.status?.status ?? 'free_readonly'
  }

  /**
   * Get current tier
   */
  @computed
  get tier(): 'pro' | 'free' | null {
    return this.status?.tier ?? null
  }

  /**
   * Get subscription limits (free tier has limits, PRO is null)
   */
  @computed
  get limits() {
    return this.status?.limits ?? null
  }

  /**
   * Get subscription usage (documents finalized this month)
   */
  @computed
  get usage() {
    return this.status?.usage ?? null
  }

  /**
   * Get monthly documents finalized (analytics)
   * Uses the correct nested field from usage object
   */
  @computed
  get monthlyDocumentsFinalized(): number {
    return this.status?.usage?.monthly_documents_finalized ?? 0
  }

  /**
   * Get documents remaining for free tier users
   * Uses the correct nested field from usage object
   */
  @computed
  get documentsRemaining(): number | null {
    return this.status?.usage?.documents_remaining ?? null
  }

  /**
   * Check if email is verified
   */
  @computed
  get emailVerified(): boolean {
    return this.status?.email_verified ?? false
  }

  /**
   * Clear billing data (logout)
   */
  clear() {
    this.status = null
    this.plans = []
    this.invoices = []
    this.error = null
  }
}
